import { useEffect } from "react";
import { useParams, Link, useLocation } from "wouter";
import {
  ActionIcon,
  Alert,
  Anchor,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Group,
  Loader,
  LoadingOverlay,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useContentPlansStore } from "./contentPlansStore";
import { IconArrowLeft, IconTrash } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import ClusterCard from "./ClusterCard";
import ClusterQuota from "./ClusterQuota";
import { clusterFromWorkstream } from "./clusterDisplay";

export default function ContentPlanDetail() {
  const { id } = useParams();
  const {
    currentPlan,
    isLoading,
    fetchContentPlan,
    clearCurrentPlan,
    deleteContentPlan,
    regenerateWorkstreams,
  } = useContentPlansStore();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (id) fetchContentPlan(id);
    return () => clearCurrentPlan();
  }, [id, fetchContentPlan, clearCurrentPlan]);

  if (isLoading && !currentPlan) {
    return (
      <Container size="lg" py="xl">
        <LoadingOverlay visible />
      </Container>
    );
  }

  if (!currentPlan) {
    return (
      <Container size="lg" py="xl">
        <Text>Plan not found</Text>
      </Container>
    );
  }

  const clusters = (currentPlan.workstreams || []).map((w) =>
    clusterFromWorkstream(w, currentPlan.id, currentPlan.name)
  );
  const generating = currentPlan.status === "generating";
  const failedPropose =
    currentPlan.status === "draft" && clusters.length === 0;

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} href="/plans">
            Clusters
          </Anchor>
          <Text>{currentPlan.name}</Text>
        </Breadcrumbs>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>{currentPlan.name}</Title>
              <Text size="sm" c="dimmed" mt="xs">
                A set of topics to research and write
              </Text>
              <Group mt="sm">
                <ClusterQuota />
              </Group>
            </div>
            <Group gap="xs">
              <Tooltip label="Delete">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    modals.openConfirmModal({
                      title: "Delete this set of clusters",
                      children: (
                        <Text size="sm">
                          Delete "{currentPlan.name}" and all its clusters?
                        </Text>
                      ),
                      labels: { confirm: "Delete", cancel: "Cancel" },
                      confirmProps: { color: "red" },
                      onConfirm: async () => {
                        const ok = await deleteContentPlan(currentPlan.id);
                        if (ok) setLocation("/plans");
                      },
                    })
                  }
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </Card>

        {generating && (
          <Alert color="blue" variant="light" icon={<Loader size={16} />}>
            Proposing clusters…
          </Alert>
        )}

        {failedPropose && (
          <Alert color="red" title="Could not propose clusters">
            <Group mt="xs">
              <Button
                size="xs"
                onClick={() => regenerateWorkstreams(currentPlan.id)}
              >
                Try again
              </Button>
            </Group>
          </Alert>
        )}

        {clusters.length > 0 && (
          <Stack gap="md">
            {clusters.map((cluster) => (
              <ClusterCard key={cluster.id} cluster={cluster} />
            ))}
          </Stack>
        )}

        {!generating && !failedPropose && clusters.length === 0 && (
          <Card withBorder padding="lg">
            <Text ta="center" c="dimmed">
              No clusters in this set.
            </Text>
          </Card>
        )}

        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          component={Link}
          href="/plans"
        >
          All clusters
        </Button>
      </Stack>
    </Container>
  );
}
